/**
 * Our Work Showcase
 * Same stacked color(top)+alpha-mask(bottom) canvas-compositing technique as
 * the Hero - Sport Rotator section (see hero-sport-rotator.js) — reused here
 * so jersey videos render with genuine per-pixel transparency over the
 * #121212 section background. Unlike Hero, there's no auto-play or rotation:
 * each card sits idle showing its poster image, and only plays its video
 * while the visitor is actively hovering (or focusing/touching) that card —
 * moving away immediately pauses it and fades back to the poster. All
 * videos are silent.
 *
 * Poster images are also auto-keyed for transparency: most poster exports
 * have a solid/flat backdrop baked in (not a true transparent PNG), so on
 * load each poster is sampled at its four corners to guess the backdrop
 * color, then any pixel close to that color is made transparent. This is a
 * heuristic, not a real alpha channel — it works well for a flat studio-style
 * backdrop, less well for busy/gradient backgrounds.
 *
 * On the FIRST hover/focus/touch, the canvas crossfades in over the poster
 * and stays visible from then on — moving away only pauses the video (freeze-
 * frame on whatever frame it was on), it does not fade back to the poster.
 * Hovering again resumes playback from that same paused frame.
 *
 * The crossfade is deliberately NOT triggered the instant you hover — it's
 * triggered the instant the FIRST real decoded video frame has been painted
 * onto the canvas. Video is loaded lazily (preload="none"), so on a fresh
 * hover there's a brief window where the video hasn't decoded any frame
 * yet; if the poster started fading out immediately, you'd see straight
 * through to the empty (transparent) canvas — i.e. the dark #121212 section
 * background — for that window, which read as a flash-to-black. Waiting for
 * a real frame before crossfading removes that gap entirely.
 */
(function () {
  'use strict';

  function averageColorAt(data, width, x0, y0, size) {
    var rSum = 0, gSum = 0, bSum = 0, count = 0;
    for (var y = y0; y < y0 + size; y++) {
      for (var x = x0; x < x0 + size; x++) {
        var i = (y * width + x) * 4;
        rSum += data[i];
        gSum += data[i + 1];
        bSum += data[i + 2];
        count++;
      }
    }
    if (!count) return [0, 0, 0];
    return [rSum / count, gSum / count, bSum / count];
  }

  function keyOutPosterBackground(canvas, img) {
    var w = img.naturalWidth;
    var h = img.naturalHeight;
    if (!w || !h) return;

    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);

    var imageData;
    try {
      imageData = ctx.getImageData(0, 0, w, h);
    } catch (e) {
      // Image failed CORS (tainted canvas) — leave the poster as-drawn,
      // un-keyed, rather than breaking the page.
      return;
    }

    var data = imageData.data;
    var sampleSize = Math.max(4, Math.floor(Math.min(w, h) * 0.03));

    var corners = [
      averageColorAt(data, w, 0, 0, sampleSize),
      averageColorAt(data, w, w - sampleSize, 0, sampleSize),
      averageColorAt(data, w, 0, h - sampleSize, sampleSize),
      averageColorAt(data, w, w - sampleSize, h - sampleSize, sampleSize)
    ];

    var bg = [0, 0, 0];
    corners.forEach(function (c) {
      bg[0] += c[0] / 4;
      bg[1] += c[1] / 4;
      bg[2] += c[2] / 4;
    });

    var innerThreshold = 26; // fully transparent below this color distance
    var outerThreshold = 60; // fully opaque above this color distance

    for (var i = 0; i < data.length; i += 4) {
      var dr = data[i] - bg[0];
      var dg = data[i + 1] - bg[1];
      var db = data[i + 2] - bg[2];
      var dist = Math.sqrt(dr * dr + dg * dg + db * db);

      if (dist <= innerThreshold) {
        data[i + 3] = 0;
      } else if (dist < outerThreshold) {
        var t = (dist - innerThreshold) / (outerThreshold - innerThreshold);
        data[i + 3] = Math.round(data[i + 3] * t);
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }

  function setupPoster(card) {
    var img = card.querySelector('[data-our-work-poster-source]');
    var canvas = card.querySelector('[data-our-work-poster-canvas]');
    if (!img || !canvas) return;

    function run() {
      keyOutPosterBackground(canvas, img);
    }

    if (img.complete && img.naturalWidth) {
      run();
    } else {
      img.addEventListener('load', run);
    }
  }

  function setupCard(card) {
    var video = card.querySelector('[data-our-work-source]');
    var canvas = card.querySelector('[data-our-work-canvas]');
    if (!video || !canvas) return null;

    var state = {
      card: card,
      video: video,
      canvas: canvas,
      ctx: null,
      bufferCanvas: null,
      bufferCtx: null,
      halfW: 0,
      halfH: 0,
      ready: false,
      revealed: false, // becomes true once the first real frame is painted
      rafId: null
    };

    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');

    video.addEventListener('loadedmetadata', function () {
      state.halfW = video.videoWidth;
      state.halfH = video.videoHeight / 2;
      if (!state.halfW || !state.halfH) return;

      state.bufferCanvas = document.createElement('canvas');
      state.bufferCanvas.width = state.halfW;
      state.bufferCanvas.height = video.videoHeight;
      state.bufferCtx = state.bufferCanvas.getContext('2d', { willReadFrequently: true });

      canvas.width = state.halfW;
      canvas.height = state.halfH;
      state.ctx = canvas.getContext('2d');

      state.ready = true;
    });

    return state;
  }

  function drawFrame(state) {
    // Wait for canvases to be sized AND for the video to actually have a
    // decoded frame available (readyState >= 2, HAVE_CURRENT_DATA). Without
    // the readyState check, the very first call(s) after play() can fire
    // before any real pixel data exists, which is what caused the black
    // flash when the crossfade started too early.
    if (!state.ready || state.video.readyState < 2) {
      state.rafId = requestAnimationFrame(function () { drawFrame(state); });
      return;
    }

    state.bufferCtx.drawImage(state.video, 0, 0, state.halfW, state.video.videoHeight);
    var full = state.bufferCtx.getImageData(0, 0, state.halfW, state.video.videoHeight).data;

    var out = state.ctx.createImageData(state.halfW, state.halfH);
    var outData = out.data;
    var maskOffset = state.halfW * state.halfH * 4;
    var count = state.halfW * state.halfH;

    for (var i = 0; i < count; i++) {
      var ci = i * 4;
      var mi = maskOffset + ci;
      outData[ci] = full[ci];
      outData[ci + 1] = full[ci + 1];
      outData[ci + 2] = full[ci + 2];
      outData[ci + 3] = full[mi];
    }

    state.ctx.putImageData(out, 0, 0);

    if (!state.revealed) {
      // First real frame is now on the canvas — safe to crossfade the
      // poster out without a black gap in between.
      state.revealed = true;
      state.card.classList.add('is-playing');
    }

    state.rafId = requestAnimationFrame(function () { drawFrame(state); });
  }

  function stopDrawing(state) {
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }
  }

  function play(state) {
    if (!state) return;
    var p = state.video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
    stopDrawing(state);
    state.rafId = requestAnimationFrame(function () { drawFrame(state); });
  }

  function pause(state) {
    if (!state) return;
    state.video.pause();
    stopDrawing(state);
  }

  // Does all the actual work for one card: keys out the poster background,
  // wires up the hidden <video>/<canvas> pair, and attaches hover/focus/touch
  // listeners. Runs immediately for every card on page load — this used to
  // be deferred via IntersectionObserver until a card was about to scroll
  // into view, but that introduced a visible bug (posters staying blank
  // until first hover in some cases), so it's back to running up front.
  // The real load-time savings already come from the video itself: it's
  // preload="none", so no video bytes download until someone actually
  // hovers/taps a card, regardless of when this setup runs.
  function activateCard(card) {
    setupPoster(card);

    var state = setupCard(card);
    if (!state) return; // no video on this block — poster just sits static, nothing to wire up

    var isPlaying = false;

    function start() {
      if (isPlaying) return;
      isPlaying = true;
      // NOTE: 'is-playing' is intentionally not added here. It gets added
      // inside drawFrame(), the moment the first real decoded frame is
      // painted onto the canvas — see the comment above drawFrame() for
      // why (avoids a black flash while the video is still loading).
      play(state);
    }

    function stop() {
      if (!isPlaying) return;
      isPlaying = false;
      // Intentionally NOT removing 'is-playing' here. Removing it would
      // fade the canvas back out and bring the poster back. Instead we
      // just pause the video + stop the draw loop, so the canvas freezes
      // on whatever frame it was on and stays visible. Hovering again
      // resumes playback from that same paused frame.
      pause(state);
    }

    // Mouse / keyboard focus (desktop)
    card.addEventListener('mouseenter', start);
    card.addEventListener('mouseleave', stop);
    card.addEventListener('focus', start);
    card.addEventListener('blur', stop);

    // Touch (mobile) — play while pressed, pause on release/cancel
    card.addEventListener('touchstart', start, { passive: true });
    card.addEventListener('touchend', stop);
    card.addEventListener('touchcancel', stop);
  }

  function initOurWork(root) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-our-work-card]'));
    cards.forEach(activateCard);
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-our-work]');
    roots.forEach(initOurWork);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    var root = evt.target.querySelector('[data-our-work]');
    if (root) initOurWork(root);
  });
})();